import { useCallback, useEffect, useMemo, useState } from 'react';
import { FieldMask } from '@weave/schema-gen-ts/dist/google/protobuf/field_mask.pb';
import { CreateResponse } from '@weave/schema-gen-ts/dist/schemas/phone/callgroup/v1/callgroup_service.pb';
import { CallGroupTypes } from '@frontend/api-call-group';
import { useForm } from '@frontend/design-system';
import { getDeviceKey } from '../components/call-group/device-key';

export const DEFAULT_CALL_GROUP_STATE: CreateResponse = {
  name: '',
  ringtone: '',
  locationIds: [],
  callGroupId: '',
  tenantId: '',
  devices: [],
};

export const buildCallGroupUpdateMaskPaths = (
  callGroupState: CreateResponse,
  initialCallGroupState: CreateResponse
): string[] => {
  const paths: string[] = [];
  if (callGroupState.name !== initialCallGroupState.name) {
    paths.push('name');
  }
  if (JSON.stringify(callGroupState.locationIds) !== JSON.stringify(initialCallGroupState.locationIds)) {
    paths.push('locationIds');
  }
  if (JSON.stringify(callGroupState.devices) !== JSON.stringify(initialCallGroupState.devices)) {
    paths.push('devices');
  }
  return paths;
};

export const buildCallGroupUpdateV2Payload = (
  callGroupState: CreateResponse,
  paths: string[]
): CallGroupTypes.UpdateV2Types['input'] => ({
  callGroupId: callGroupState.callGroupId,
  locationIds: callGroupState.locationIds,
  name: callGroupState.name,
  devices: callGroupState.devices,
  updateMask: new FieldMask({ paths }),
});

/**
 * Shared state and handlers for the batched call group save flow, used by both the
 * full-page call group settings view and the call-route step tray editor.
 */
export const useCallGroupBatchedState = () => {
  const [callGroupState, setCallGroupState] = useState<CreateResponse>(DEFAULT_CALL_GROUP_STATE);
  const [initialCallGroupState, setInitialCallGroupState] = useState<CreateResponse>(DEFAULT_CALL_GROUP_STATE);

  const form = useForm({
    computeChangedValues: true,
    fields: {
      name: { type: 'text' },
    },
  });

  const nameField = form.getFieldProps('name');

  useEffect(() => {
    if (nameField.value !== undefined) {
      setCallGroupState((prevState) => ({
        ...prevState,
        name: nameField.value,
      }));
    }
  }, [nameField.value]);

  const resetCallGroupState = useCallback((newState: CreateResponse) => {
    setCallGroupState(newState);
    setInitialCallGroupState(newState);
  }, []);

  const hasUnsavedChanges = useMemo(() => {
    const hasFormChanges = !!form.changedValues;
    const hasStateChanges =
      JSON.stringify({ ...initialCallGroupState, name: nameField.value }) !== JSON.stringify(callGroupState);
    return hasFormChanges || hasStateChanges;
  }, [form.changedValues, initialCallGroupState, callGroupState, nameField.value]);

  const updateMaskPaths = useMemo(
    () => buildCallGroupUpdateMaskPaths(callGroupState, initialCallGroupState),
    [callGroupState, initialCallGroupState]
  );

  const handleRemoveDevice = useCallback((deviceKey: string) => {
    setCallGroupState((prevState) => ({
      ...prevState,
      devices: prevState.devices.filter((device) => getDeviceKey(device) !== deviceKey),
    }));
  }, []);

  const handleRingChange = useCallback((deviceKey: string, delayStart: number, timeout: number) => {
    setCallGroupState((prevState) => ({
      ...prevState,
      devices: prevState.devices.map((device) =>
        getDeviceKey(device) === deviceKey ? { ...device, delayStart, timeout } : device
      ),
    }));
  }, []);

  const handleDevicesAssigned = useCallback((devices: CreateResponse['devices']) => {
    setCallGroupState((prevState) => ({ ...prevState, devices }));
  }, []);

  return {
    callGroupState,
    setCallGroupState,
    initialCallGroupState,
    setInitialCallGroupState,
    resetCallGroupState,
    form,
    nameField,
    hasUnsavedChanges,
    updateMaskPaths,
    handleRemoveDevice,
    handleRingChange,
    handleDevicesAssigned,
  };
};
